import { useState, useRef, useCallback, useEffect } from 'react';
import { 
    MessageSquarePlus, Camera, Send, X, Bug, Lightbulb, HelpCircle, 
    ImagePlus, Clipboard, Scissors, PenTool, Sparkles, Loader2 
} from 'lucide-react';
import { cn, getUploadUrl, validateUploadFile, MAX_MEDIA_IMAGE_SIZE, extractErrorMessage } from '@/lib/utils';
import { useCurrentModule } from '@/hooks/useCurrentModule';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { trpcClient } from '@/lib/trpc';
import { toast } from 'sonner';
import { uploadApi } from '@/lib/api-client';
import { RegionSelector } from './RegionSelector';
import { ScreenshotAnnotator } from './ScreenshotAnnotator';
import { captureWithNativeTab } from './screenshotEngines';
import type { FeedbackType, FeedbackPriority } from '@/shared/schemas/feedback.schema';
import {
    FEEDBACK_TYPES,
    FEEDBACK_TYPE_LABELS,
    FEEDBACK_PRIORITIES,
    FEEDBACK_PRIORITY_LABELS,
} from '@/shared/schemas/feedback.schema';
import { FEEDBACK_DESCRIPTION_HINT, FEEDBACK_MAX_IMAGES } from '@/shared/schemas/feedbackStatus';
import {
    FeedbackImagePickerButtons,
    FeedbackThumbGrid,
    useIsTouchDevice,
} from '@/components/feedback/feedback-ui';
import {
    getDiagnosticsSnapshot,
    getDiagnosticsSummaryText,
    getSuggestedTitleFromDiagnostics,
    getCurrentAppVersion,
    type DiagnosticsSnapshot,
} from '@/lib/diagnostics';

const TYPE_ICONS: Record<FeedbackType, typeof Bug> = { BUG: Bug, IMPROVEMENT: Lightbulb, QUESTION: HelpCircle };

// Helper: Chuyển đổi DataURL Base64 sang đối tượng File nhị phân chuẩn
function dataUrlToFile(dataUrl: string, filename: string): File | null {
    try {
        const arr = dataUrl.split(',');
        const mimeMatch = arr[0].match(/:(.*?);/);
        if (!mimeMatch) return null;
        const mime = mimeMatch[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], filename, { type: mime });
    } catch (err) {
        console.error('[FeedbackWidget] dataUrlToFile error:', err);
        return null;
    }
}

export default function FeedbackWidget() {
    const [isOpen, setIsOpen] = useState(false);
    const [showCaptureOverlay, setShowCaptureOverlay] = useState(false);
    const [showAnnotator, setShowAnnotator] = useState(false);
    
    // Screenshot states
    const [originalFullScreenshot, setOriginalFullScreenshot] = useState<string | null>(null);
    const [rawScreenshot, setRawScreenshot] = useState<string | null>(null);
    const [recropImage, setRecropImage] = useState<string | null>(null);
    const [annotatingIndex, setAnnotatingIndex] = useState<number>(0);
    const isRecroppingRef = useRef<boolean>(false);

    // Form data & attachments
    const [images, setImages] = useState<string[]>([]);
    const [type, setType] = useState<FeedbackType>('BUG');
    const [priority, setPriority] = useState<FeedbackPriority>('MEDIUM');
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [moduleName, setModuleName] = useState('');
    const [pageUrl, setPageUrl] = useState('');
    const [attachDiagnostics, setAttachDiagnostics] = useState(true);
    const [diagSnapshot, setDiagSnapshot] = useState<DiagnosticsSnapshot | null>(null);
    const [editingFeedbackId, setEditingFeedbackId] = useState<string | null>(null);
    const isTouch = useIsTouchDevice();

    const { moduleTitle, groupTitle } = useCurrentModule();
    const location = useLocation();

    // Module + Trang + Diagnostics snapshot tự điền mỗi lần mở (người dùng sửa được)
    useEffect(() => {
        if (isOpen) {
            const snap = getDiagnosticsSnapshot();
            setDiagSnapshot(snap);
            if (!moduleName && !pageUrl) {
                setModuleName(`${groupTitle} > ${moduleTitle}`);
                setPageUrl(location.pathname);
            }
            if (!title.trim()) {
                const suggested = getSuggestedTitleFromDiagnostics(snap);
                if (suggested) setTitle(suggested);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const createFeedbackMutation = useMutation({
        mutationFn: async (vars: any) => trpcClient.feedback.create.mutate(vars),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['feedbacks'] });
            window.dispatchEvent(new Event('feedback-created'));
        }
    });

    const updateFeedbackMutation = useMutation({
        mutationFn: async (vars: any) => trpcClient.feedback.update.mutate(vars),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['feedbacks'] });
        }
    });

    // Lắng nghe sự kiện mở widget từ các nơi khác (ví dụ ErrorComponent "Thêm mô tả")
    useEffect(() => {
        const handleOpen = (e: Event) => {
            const detail = (e as CustomEvent)?.detail;
            if (detail?.feedbackId) {
                setEditingFeedbackId(detail.feedbackId);
            }
            if (detail?.title) {
                setTitle(detail.title);
            }
            setIsOpen(true);
        };
        window.addEventListener('open-feedback-widget', handleOpen);
        return () => window.removeEventListener('open-feedback-widget', handleOpen);
    }, []);

    // Upload file ảnh tĩnh lên server (Anti-Base64)
    // Nếu replaceIndex !== undefined -> thay thế đúng vị trí ảnh đang sửa (ví dụ: images[0] = screenshotBefore khi cắt lại hoặc vẽ chú thích)
    const uploadImageFile = async (file: File, replaceIndex?: number) => {
        const isReplacing = typeof replaceIndex === 'number' && replaceIndex >= 0 && replaceIndex < images.length;
        if (!isReplacing && images.length >= FEEDBACK_MAX_IMAGES) {
            toast.warning('Chỉ được đính kèm tối đa 6 ảnh');
            return;
        }
        const validation = validateUploadFile(file, MAX_MEDIA_IMAGE_SIZE, 'Ảnh phản hồi');
        if (!validation.valid) {
            toast.error(validation.error);
            return;
        }
        setIsUploading(true);
        try {
            const { fileUrl } = await uploadApi.uploadFile(file);
            setImages(prev => {
                if (typeof replaceIndex === 'number' && replaceIndex >= 0 && replaceIndex < prev.length) {
                    const next = [...prev];
                    next[replaceIndex] = fileUrl;
                    return next;
                }
                return prev.length < 6 ? [...prev, fileUrl] : prev;
            });
            toast.success(isReplacing ? 'Đã cập nhật ảnh chụp màn hình' : 'Đã tải ảnh lên thành công');
        } catch (error) {
            console.error('[FeedbackWidget] Upload error:', error);
            toast.error('Lỗi upload ảnh lên máy chủ: ' + extractErrorMessage(error));
        } finally {
            setIsUploading(false);
        }
    };

    // ─── 1. CHỤP SIÊU NÉT (Native Tab Capture - 100% WYSIWYG) ─────────────
    const handleNativeCapture = async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        isRecroppingRef.current = false;
        setRecropImage(null);

        // Ẩn tức thì panel feedback trên DOM để chống dính bóng mờ vào ảnh
        const panel = document.getElementById('feedback-widget-panel');
        const backdrop = document.getElementById('feedback-widget-backdrop');
        if (panel) panel.style.display = 'none';
        if (backdrop) backdrop.style.display = 'none';
        setIsOpen(false);

        toast.info('💡 Vui lòng bấm [Chia sẻ thẻ này / Share this tab] để chụp chuẩn nét 100%', { duration: 3500 });

        try {
            const dataUrl = await captureWithNativeTab();
            setOriginalFullScreenshot(dataUrl);
            setRecropImage(dataUrl);
            setShowCaptureOverlay(true);
        } catch (err: any) {
            console.warn('[FeedbackWidget] Native capture cancelled or unsupported, fallback to canvas:', err);
            toast.info('Đã chuyển sang chế độ chụp nhanh');
            setRecropImage(null);
            setTimeout(() => {
                setShowCaptureOverlay(true);
            }, 40);
        }
    };

    // ─── 2. CHỤP NHANH (Silent Canvas Capture) ───────────────────────────
    const handleCanvasCapture = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        isRecroppingRef.current = false;
        setRecropImage(null);

        // Ẩn tức thì panel feedback trên DOM
        const panel = document.getElementById('feedback-widget-panel');
        const backdrop = document.getElementById('feedback-widget-backdrop');
        if (panel) panel.style.display = 'none';
        if (backdrop) backdrop.style.display = 'none';
        setIsOpen(false);

        setTimeout(() => setShowCaptureOverlay(true), 40);
    };

    // ─── 3. CẮT LẠI (Recrop từ ảnh gốc) ──────────────────────────────────
    const handleRecrop = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const sourceImage = originalFullScreenshot || rawScreenshot;
        if (!sourceImage) return;

        isRecroppingRef.current = true;
        setRecropImage(sourceImage);
        setIsOpen(false);
        setTimeout(() => setShowCaptureOverlay(true), 40);
    };

    // ─── 4. KHI CHỤP / CROP XONG ─────────────────────────────────────────
    const handleCaptureComplete = useCallback(async (dataUrl: string) => {
        setShowCaptureOverlay(false);
        setRecropImage(null);
        const shouldReplaceCover = isRecroppingRef.current && images.length > 0;
        isRecroppingRef.current = false;

        // Nếu chưa lưu ảnh gốc thì gán ảnh này làm ảnh gốc để recrop
        if (!originalFullScreenshot) {
            setOriginalFullScreenshot(dataUrl);
        }
        setRawScreenshot(dataUrl);
        setIsOpen(true);

        // Tự động upload file tĩnh ngầm (Anti-Base64)
        // Khi cắt lại (Recrop) hoặc chụp đè ảnh bìa -> thay thế images[0] (screenshotBefore)
        if (dataUrl.startsWith('data:image/')) {
            const file = dataUrlToFile(dataUrl, `fb_${Date.now()}.png`);
            if (file) {
                await uploadImageFile(file, shouldReplaceCover ? 0 : undefined);
            }
        }
    }, [originalFullScreenshot, images.length]);

    const handleCaptureCancel = useCallback(() => {
        setShowCaptureOverlay(false);
        setRecropImage(null);
        isRecroppingRef.current = false;
        setIsOpen(true);
    }, []);

    // ─── 5. LƯU CHÚ THÍCH TỪ ANNOTATOR ────────────────────────────────────
    const handleAnnotatorSave = async (annotatedDataUrl: string) => {
        setShowAnnotator(false);
        const targetIdx = annotatingIndex >= 0 && annotatingIndex < images.length ? annotatingIndex : 0;
        if (targetIdx === 0) {
            setRawScreenshot(annotatedDataUrl);
        }

        if (annotatedDataUrl.startsWith('data:image/')) {
            const file = dataUrlToFile(annotatedDataUrl, `fb_annotated_${Date.now()}.png`);
            if (file) {
                await uploadImageFile(file, images.length > 0 ? targetIdx : undefined);
            }
        }
    };

    // ─── 6. DÁN ẢNH TỪ CLIPBOARD (Ctrl+V) ─────────────────────────────────
    const handlePaste = useCallback(async (e: React.ClipboardEvent) => {
        const items = e.clipboardData?.items;
        if (!items) return;

        for (const item of Array.from(items)) {
            if (item.type.startsWith('image/')) {
                e.preventDefault();
                const blob = item.getAsFile();
                if (!blob) continue;
                await uploadImageFile(blob as File);
                break;
            }
        }
    }, [images.length]);

    // ─── 7. CHỌN ẢNH TỪ FILE BROWSER ─────────────────────────────────────
    const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        for (const file of files) {
            if (file.type.startsWith('image/')) {
                await uploadImageFile(file);
            }
        }
        e.target.value = ''; // Reset input
    }, [images.length]);

    // ─── 7b. KÉO THẢ ẢNH (desktop) ───────────────────────────────────────
    const handleDrop = useCallback(async (e: React.DragEvent) => {
        const files = Array.from(e.dataTransfer?.files || []).filter(f => f.type.startsWith('image/'));
        if (!files.length) return;
        e.preventDefault();
        for (const file of files) {
            await uploadImageFile(file);
        }
    }, [images.length]);

    // ─── 8. GỬI FEEDBACK ─────────────────────────────────────────────────
    const handleSubmit = async () => {
        if (!title.trim()) {
            toast.error('Vui lòng nhập tiêu đề phản hồi');
            return;
        }

        setIsSubmitting(true);
        try {
            const latestSnap = attachDiagnostics ? getDiagnosticsSnapshot() : undefined;
            if (editingFeedbackId) {
                const res = await updateFeedbackMutation.mutateAsync({
                    id: editingFeedbackId,
                    module: moduleName.trim() || `${groupTitle} > ${moduleTitle}`,
                    pageUrl: pageUrl.trim() || location.pathname,
                    type,
                    priority,
                    title: title.trim(),
                    description: description.trim(),
                    images,
                });
                toast.success(`Đã cập nhật mô tả cho phiếu #${(res as any)?.code || ''}`, {
                    action: editingFeedbackId
                        ? { label: 'Mở phiếu', onClick: () => navigate({ to: `/feedback/${editingFeedbackId}` as string }) }
                        : undefined,
                });
            } else {
                const res = await createFeedbackMutation.mutateAsync({
                    module: moduleName.trim() || `${groupTitle} > ${moduleTitle}`,
                    pageUrl: pageUrl.trim() || location.pathname,
                    type,
                    priority,
                    title: title.trim(),
                    description: description.trim(),
                    images,
                    source: 'USER',
                    diagnostics: latestSnap,
                    // Chuẩn v2: mã phiên tab để dev xem "Nhật ký sự kiện" (tắt đính kèm thì không gửi)
                    sessionId: latestSnap?.sessionId,
                    appVersion: getCurrentAppVersion(),
                    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
                });

                toast.success(`Đã gửi phản hồi #${res?.code || ''}`, {
                    description: 'Cảm ơn bạn đã góp ý.',
                    action: res?.id
                        ? { label: 'Mở phiếu', onClick: () => navigate({ to: `/feedback/${res.id}` as string }) }
                        : undefined,
                });
            }

            // Reset form
            setTitle('');
            setDescription('');
            setType('BUG');
            setPriority('MEDIUM');
            setImages([]);
            setRawScreenshot(null);
            setOriginalFullScreenshot(null);
            setModuleName('');
            setPageUrl('');
            setEditingFeedbackId(null);
            setAttachDiagnostics(true);
            setIsOpen(false);
        } catch (error: any) {
            toast.error('Không thể gửi phản hồi: ' + extractErrorMessage(error));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            {/* Camera Viewfinder Screen Capture (Kính ngắm máy ảnh & Recrop) */}
            {showCaptureOverlay && (
                <RegionSelector
                    initialImage={recropImage || undefined}
                    onCapture={handleCaptureComplete}
                    onCancel={handleCaptureCancel}
                />
            )}

            {/* Annotator Modal (Vẽ / Sửa ảnh ghi chú lỗi) */}
            {showAnnotator && (rawScreenshot || (images.length > 0 && images[annotatingIndex])) && (
                <ScreenshotAnnotator
                    image={(annotatingIndex === 0 && rawScreenshot) ? rawScreenshot : getUploadUrl(images[annotatingIndex] || images[0])}
                    onSave={handleAnnotatorSave}
                    onCancel={() => setShowAnnotator(false)}
                />
            )}

            {/* Hidden file input */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                data-testid="fb-widget-desktop-file-input"
                onChange={handleFileSelect}
            />

            {/* FAB Button — bottom-16 to avoid page nav overlap */}
            {!isOpen && !showCaptureOverlay && (
                <button
                    type="button"
                    data-testid="fb-widget-fab"
                    aria-label="Gửi phản hồi"
                    onClick={() => setIsOpen(true)}
                    className={cn(
                        'fixed bottom-[calc(88px+env(safe-area-inset-bottom))] right-4 md:bottom-16 md:right-6 z-50 h-14 w-14 md:h-12 md:w-12 rounded-full shadow-lg cursor-pointer',
                        'bg-gradient-to-br from-blue-500 to-indigo-600 text-white',
                        'hover:from-blue-600 hover:to-indigo-700 hover:shadow-xl hover:scale-105',
                        'transition-all duration-200 flex items-center justify-center',
                        'print:hidden',
                    )}
                    title="Gửi phản hồi / Báo lỗi"
                >
                    <MessageSquarePlus className="h-5 w-5" />
                </button>
            )}

            {/* Feedback Panel */}
            {isOpen && !showCaptureOverlay && (
                <>
                    {/* Backdrop */}
                    <div
                        id="feedback-widget-backdrop"
                        data-feedback-ignore="true"
                        className="fixed inset-0 bg-black/30 z-[70] animate-in fade-in duration-150 print:hidden"
                        onClick={() => setIsOpen(false)}
                    />

                    {/* Main Panel */}
                    <div
                        id="feedback-widget-panel"
                        data-feedback-ignore="true"
                        role="dialog"
                        aria-label="Gửi phản hồi"
                        data-testid="fb-widget-panel"
                        className="fixed z-[71] inset-x-0 bottom-0 w-full max-h-[90vh] rounded-t-2xl md:inset-x-auto md:bottom-16 md:right-6 md:w-[400px] md:max-h-[85vh] md:rounded-2xl bg-white shadow-2xl border border-slate-200 flex flex-col overflow-hidden print:hidden animate-in slide-in-from-bottom-4 fade-in duration-200"
                        onPaste={handlePaste}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={handleDrop}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 text-white shadow-sm">
                            <div className="flex items-center gap-2">
                                <MessageSquarePlus className="h-4 w-4" />
                                <span className="font-semibold text-sm">Gửi phản hồi</span>
                            </div>
                            <button
                                type="button"
                                onClick={() => setIsOpen(false)}
                                className="h-11 w-11 md:h-8 md:w-8 -mr-2 flex items-center justify-center rounded-lg hover:bg-white/20 transition-colors cursor-pointer"
                                title="Đóng"
                                aria-label="Đóng"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>

                        {/* Form Body */}
                        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3.5">
                            {/* Loại */}
                            <div>
                                <label className="text-sm font-semibold text-slate-700 mb-1 block">Loại</label>
                                <div className="flex flex-wrap gap-1.5">
                                    {FEEDBACK_TYPES.map(t => {
                                        const Icon = TYPE_ICONS[t];
                                        return (
                                            <button
                                                key={t}
                                                type="button"
                                                data-testid={`fb-widget-type-${t}`}
                                                aria-pressed={type === t}
                                                onClick={() => setType(t)}
                                                className={cn(
                                                    'min-h-11 md:min-h-9 px-3 rounded-lg border text-sm font-medium inline-flex items-center gap-1.5 cursor-pointer',
                                                    type === t ? 'bg-blue-50 border-blue-500 text-blue-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50',
                                                )}
                                            >
                                                <Icon className="h-4 w-4" />
                                                {FEEDBACK_TYPE_LABELS[t]}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Mức độ */}
                            <div>
                                <label className="text-sm font-semibold text-slate-700 mb-1 block">Mức độ</label>
                                <div className="grid grid-cols-4 gap-1.5">
                                    {FEEDBACK_PRIORITIES.map(p => (
                                        <button
                                            key={p}
                                            type="button"
                                            data-testid={`fb-widget-priority-${p}`}
                                            aria-pressed={priority === p}
                                            onClick={() => setPriority(p as FeedbackPriority)}
                                            className={cn(
                                                'min-h-11 md:min-h-9 px-1 rounded-lg border text-sm font-medium cursor-pointer',
                                                priority === p ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-200 text-slate-600 hover:bg-slate-50',
                                            )}
                                        >
                                            {FEEDBACK_PRIORITY_LABELS[p]}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Title */}
                            <div>
                                <label className="text-sm font-semibold text-slate-700 mb-1 block">
                                    Tiêu đề <span className="text-rose-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    data-testid="fb-widget-title"
                                    maxLength={200}
                                    value={title}
                                    onChange={e => setTitle(e.target.value)}
                                    placeholder="VD: Nút xác nhận đơn hàng không phản hồi..."
                                    className="w-full h-11 md:h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
                                />
                            </div>

                            {/* Description */}
                            <div>
                                <label className="text-sm font-semibold text-slate-700 mb-1 block">
                                    Mô tả
                                </label>
                                <textarea
                                    data-testid="fb-widget-description"
                                    value={description}
                                    onChange={e => setDescription(e.target.value)}
                                    placeholder={FEEDBACK_DESCRIPTION_HINT}
                                    rows={4}
                                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
                                />
                            </div>

                            {/* Module + Trang (tự điền, sửa được) */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                <div className="min-w-0">
                                    <label htmlFor="fb-widget-module" className="text-sm font-semibold text-slate-700 mb-1 block">Module</label>
                                    <input
                                        id="fb-widget-module"
                                        data-testid="fb-widget-module"
                                        value={moduleName}
                                        onChange={e => setModuleName(e.target.value)}
                                        className="w-full h-11 md:h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                                    />
                                </div>
                                <div className="min-w-0">
                                    <label htmlFor="fb-widget-page" className="text-sm font-semibold text-slate-700 mb-1 block">Trang</label>
                                    <input
                                        id="fb-widget-page"
                                        data-testid="fb-widget-page"
                                        value={pageUrl}
                                        onChange={e => setPageUrl(e.target.value)}
                                        className="w-full h-11 md:h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                                    />
                                </div>
                            </div>

                            {/* Screenshot Section */}
                            <div>
                                <div className="flex items-center justify-between mb-1.5">
                                    <label className="text-sm font-semibold text-slate-700">
                                        Ảnh ({images.length}/{FEEDBACK_MAX_IMAGES})
                                    </label>
                                </div>

                                {/* Thiết bị cảm ứng: Chụp ảnh + Chọn từ thư viện (ẩn chụp vùng màn hình) */}
                                {isTouch && (
                                    <div className="space-y-2" data-testid="fb-widget-touch-controls">
                                        <FeedbackImagePickerButtons
                                            testIdPrefix="fb-widget"
                                            onFiles={async (files) => { for (const f of files) await uploadImageFile(f); }}
                                            isUploading={isUploading}
                                            disabled={isSubmitting || isUploading || images.length >= FEEDBACK_MAX_IMAGES}
                                        />
                                        <FeedbackThumbGrid
                                            testId="fb-widget-thumbs"
                                            images={images}
                                            isUploading={isUploading}
                                            onRemove={(idx) => setImages(prev => prev.filter((_, i) => i !== idx))}
                                        />
                                    </div>
                                )}

                                {/* Desktop: chụp vùng màn hình + vẽ đánh dấu + dán Ctrl+V + kéo thả + chọn file */}
                                {!isTouch && (
                                <div className="space-y-2" data-testid="fb-widget-desktop-controls">
                                    <div className="flex gap-1.5">
                                        {/* Nút Chụp siêu nét (HD) */}
                                        <button
                                            type="button"
                                            onClick={handleNativeCapture}
                                            disabled={isSubmitting || isUploading || images.length >= 6}
                                            className="flex-1 flex items-center justify-center gap-1.5 px-2.5 py-2 text-xs font-semibold bg-sky-600 hover:bg-sky-500 text-white rounded-lg shadow-sm transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                            title="Chụp từ tab trình duyệt: 100% chuẩn nét từng pixel, không lệch font"
                                        >
                                            <Camera className="h-3.5 w-3.5" />
                                            <span>Chụp HD</span>
                                        </button>

                                        {/* Nút Chụp nhanh */}
                                        <button
                                            type="button"
                                            onClick={handleCanvasCapture}
                                            disabled={isSubmitting || isUploading || images.length >= 6}
                                            className="flex-1 flex items-center justify-center gap-1.5 px-2.5 py-2 text-xs font-medium border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 rounded-lg shadow-sm transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                            title="Chụp nhanh tức thì trong nền (không hiện popup trình duyệt)"
                                        >
                                            <Sparkles className="h-3.5 w-3.5 text-amber-500" />
                                            <span>Chụp nhanh</span>
                                        </button>

                                        {/* Nút Chọn file */}
                                        <button
                                            type="button"
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={isSubmitting || isUploading || images.length >= 6}
                                            className="p-2 border border-slate-300 bg-white hover:bg-slate-50 text-slate-600 rounded-lg shadow-sm transition-colors cursor-pointer disabled:opacity-50"
                                            title="Chọn ảnh từ máy"
                                            aria-label="Chọn ảnh từ máy"
                                            data-testid="fb-widget-desktop-file-btn"
                                        >
                                            <ImagePlus className="h-3.5 w-3.5" />
                                        </button>

                                        {/* Nút Hướng dẫn dán ảnh */}
                                        <button
                                            type="button"
                                            onClick={() => toast.warning('Nhấn Ctrl+V / ⌘+V để dán ảnh từ clipboard vào form', { duration: 4000 })}
                                            className="p-2 border border-slate-300 bg-white hover:bg-slate-50 text-slate-600 rounded-lg shadow-sm transition-colors cursor-pointer"
                                            title="Dán ảnh (Ctrl+V)"
                                        >
                                            <Clipboard className="h-3.5 w-3.5" />
                                        </button>
                                    </div>

                                    {/* Sub-label */}
                                    <div className="text-xs text-slate-500 px-0.5">
                                        Chụp vùng màn hình, dán Ctrl+V hoặc kéo thả ảnh vào đây.
                                    </div>

                                    {/* Uploaded Images Grid */}
                                    {images.length > 0 && (
                                        <div className="grid grid-cols-3 gap-1.5 p-2 bg-slate-50 rounded-lg border border-slate-200 max-h-[160px] overflow-y-auto">
                                            {images.map((imgUrl, idx) => (
                                                <div
                                                    key={idx}
                                                    className="relative rounded-md border border-slate-200 overflow-hidden bg-white aspect-video group"
                                                >
                                                    <img
                                                        src={getUploadUrl(imgUrl)}
                                                        alt={`Thumb ${idx + 1}`}
                                                        className="w-full h-full object-cover"
                                                    />

                                                    {/* Hover Overlay Controls */}
                                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                                                        {/* Nút Cắt lại từ ảnh gốc */}
                                                        {idx === 0 && (originalFullScreenshot || rawScreenshot) && (
                                                            <button
                                                                type="button"
                                                                onClick={handleRecrop}
                                                                className="p-1 bg-sky-600 text-white rounded text-[10px] shadow hover:bg-sky-500 transition-colors cursor-pointer"
                                                                title="Cắt lại vùng chọn từ ảnh gốc"
                                                            >
                                                                <Scissors className="h-3 w-3" />
                                                            </button>
                                                        )}

                                                        {/* Nút Vẽ / Sửa */}
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setAnnotatingIndex(idx);
                                                                setShowAnnotator(true);
                                                            }}
                                                            className="p-1 bg-blue-600 text-white rounded text-[10px] shadow hover:bg-blue-500 transition-colors cursor-pointer"
                                                            title="Vẽ / Ghi chú trên ảnh"
                                                        >
                                                            <PenTool className="h-3 w-3" />
                                                        </button>

                                                        {/* Nút Xóa */}
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setImages(prev => prev.filter((_, i) => i !== idx));
                                                                if (idx === 0) {
                                                                    setRawScreenshot(null);
                                                                    setOriginalFullScreenshot(null);
                                                                }
                                                            }}
                                                            className="p-1 bg-rose-600 text-white rounded text-[10px] shadow hover:bg-rose-500 transition-colors cursor-pointer"
                                                            title="Xóa ảnh này"
                                                        >
                                                            <X className="h-3 w-3" />
                                                        </button>
                                                    </div>

                                                    {idx === 0 && (
                                                        <span className="absolute bottom-0.5 left-0.5 bg-blue-600 text-white text-[8px] font-bold px-1 rounded pointer-events-none">
                                                            Bìa
                                                        </span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Recrop & Annotate Action Bar if latest image available */}
                                    {images.length > 0 && (originalFullScreenshot || rawScreenshot) && (
                                        <div className="flex items-center justify-between px-1 py-0.5 text-xs text-slate-600">
                                            <button
                                                type="button"
                                                onClick={handleRecrop}
                                                className="text-[11px] text-sky-600 hover:text-sky-700 font-medium flex items-center gap-1 cursor-pointer"
                                            >
                                                <Scissors className="h-3 w-3" /> Cắt lại từ ảnh gốc
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setAnnotatingIndex(0);
                                                    setShowAnnotator(true);
                                                }}
                                                className="text-[11px] text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1 cursor-pointer"
                                            >
                                                <PenTool className="h-3 w-3" /> Vẽ ghi chú
                                            </button>
                                        </div>
                                    )}
                                </div>
                                )}
                            </div>

                            {/* Đính kèm thông tin chẩn đoán */}
                            <div className="rounded-lg border border-slate-200 bg-slate-50/80 p-2.5">
                                <label className="flex items-start gap-2.5 cursor-pointer select-none">
                                    <input
                                        type="checkbox"
                                        data-testid="fb-widget-attach-diagnostics"
                                        checked={attachDiagnostics}
                                        onChange={e => setAttachDiagnostics(e.target.checked)}
                                        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <div className="min-w-0 flex-1">
                                        <div className="text-xs font-semibold text-slate-700">
                                            Đính kèm thao tác gần đây & thông tin chẩn đoán
                                        </div>
                                        <div
                                            data-testid="fb-widget-diagnostics-summary"
                                            className="text-[11px] text-slate-500 mt-0.5 truncate"
                                        >
                                            {getDiagnosticsSummaryText(diagSnapshot || undefined)}
                                        </div>
                                    </div>
                                </label>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] border-t border-slate-100 bg-slate-50 flex items-center justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setIsOpen(false)}
                                className="min-h-11 md:min-h-9 px-4 rounded-lg border border-slate-300 bg-white text-sm font-medium text-slate-700 cursor-pointer"
                            >
                                Hủy
                            </button>
                            <button
                                type="button"
                                data-testid="fb-widget-submit"
                                onClick={handleSubmit}
                                disabled={isSubmitting || isUploading || !title.trim()}
                                className={cn(
                                    'min-h-11 md:min-h-9 flex items-center gap-1.5 px-4 rounded-lg text-sm font-semibold transition-all cursor-pointer',
                                    'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md',
                                    'hover:from-blue-700 hover:to-indigo-700 hover:shadow-lg',
                                    'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-md',
                                )}
                            >
                                {isSubmitting || isUploading ? (
                                    <>
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                        <span>Đang gửi...</span>
                                    </>
                                ) : (
                                    <>
                                        <Send className="h-3.5 w-3.5" />
                                        <span>Gửi phản hồi</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}
